import React, { useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import { Map } from 'lucide-react';
import { CreatePropertyDto } from '../../../handling_data/dtos/PropertyDto';
import { StepRef } from './BasicInfoStep';

interface LocationStepProps {
    t: any;
    formData: CreatePropertyDto;
}

const LocationStep = forwardRef<StepRef, LocationStepProps>(({ t, formData }, ref) => {
    const [localData, setLocalData] = useState({
        ar: { ...formData.ar },
        en: { ...formData.en },
        tr: { ...formData.tr },
        lat: formData.lat,
        lng: formData.lng
    });

    // Sync local state when formData changes (essential for edit flow)
    useEffect(() => {
        setLocalData({
            ar: { ...formData.ar },
            en: { ...formData.en },
            tr: { ...formData.tr },
            lat: formData.lat,
            lng: formData.lng
        });
    }, [formData.ar, formData.en, formData.tr, formData.lat, formData.lng]);

    useImperativeHandle(ref, () => ({
        getData: () => localData
    }));

    const updateAddress = (lang: 'ar' | 'en' | 'tr', value: string) => {
        setLocalData(prev => ({
            ...prev,
            [lang]: { ...prev[lang], address: value }
        }));
    };

    return (
        <div className="space-y-4 animate-fade-in">
            <h3 className="text-lg font-bold text-gray-800 border-b pb-2 mb-4">{t('property_form.location')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {(['ar', 'en', 'tr'] as const).map(lang => (
                    <div key={lang}>
                        <label className="block text-xs font-medium text-gray-500 mb-1">{t(`property_form.address_${lang}`)}</label>
                        <input
                            type="text"
                            className={`w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-primary/20 ${lang === 'en' ? 'dir-ltr' : ''}`}
                            value={localData[lang].address || ''}
                            onChange={(e) => updateAddress(lang, e.target.value)}
                        />
                    </div>
                ))}
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('property_form.lat')}</label>
                    <input
                        type="number"
                        step="any"
                        className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-primary/20 dir-ltr"
                        value={localData.lat}
                        onChange={(e) => setLocalData({ ...localData, lat: Number(e.target.value) })}
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('property_form.lng')}</label>
                    <input
                        type="number"
                        step="any"
                        className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-primary/20 dir-ltr"
                        value={localData.lng}
                        onChange={(e) => setLocalData({ ...localData, lng: Number(e.target.value) })}
                    />
                </div>
            </div>
            <div className="h-64 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 border-2 border-dashed border-gray-300">
                <div className="text-center">
                    <Map className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <span>{t('property_form.map_placeholder')}</span>
                </div>
            </div>
        </div>
    );
});

export default LocationStep;
